import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Footer from '../components/Footer';
import { cartStorage, CartItem } from '../utils/cartStorage';
import { pushAddToCart } from '../utils/gtm';
import { computeOrderTotals, formatMoney, planPricingFromPlan, OrderTotals, PlanPricingInput } from '../utils/orderTotals';

const PHONE_DISPLAY = '1-877-579-9862';
const PHONE_TEL = 'tel:1-877-579-9862';

interface PricingPlan {
  id: string;
  planType: string;
  name: string;
  monthlyPrice: number;
  annualTotal: number | null;
  billingCycle: string;
}

interface Product {
  id: string;
  name: string;
  shortName: string;
  description: string;
  imageFilename: string | null;
  category: string;
  hasFallDetection: boolean;
  features: string[];
  pricingPlans: PricingPlan[];
}

interface Accessory {
  id: string;
  name: string;
  description: string | null;
  price: number;
  priceType: string;
  priceUnit: string | null;
  imageFilename: string | null;
  shortName: string | null;
}

interface CartOrderSummaryProps {
  totals: OrderTotals;
  planPricing: PlanPricingInput | null;
  planPriceUnavailable: boolean;
  onRetryPricing: () => void;
}

/**
 * Monthly price, shipping and amount due today, computed with the same
 * computeOrderTotals helper checkout uses so the two pages always agree.
 */
const CartOrderSummary: React.FC<CartOrderSummaryProps> = ({ totals, planPricing, planPriceUnavailable, onRetryPricing }) => {
  if (planPriceUnavailable) {
    return (
      <div className="cart-order-summary cart-order-summary--unavailable" data-testid="cart-price-unavailable">
        <strong>We couldn&apos;t load the price for this plan.</strong>
        <div className="cart-order-summary-actions">
          <button type="button" className="cart-retry-btn" onClick={onRetryPricing}>Try Again</button>
          <a href={PHONE_TEL} className="cart-phone-link">or call <span className="checkout-phone-number">{PHONE_DISPLAY}</span></a>
        </div>
      </div>
    );
  }

  const isAnnual = planPricing?.billingCycle === 'annual';
  const shippingLabel = totals.hasProduct
    ? (totals.shippingFeeToday > 0 ? formatMoney(totals.shippingFeeToday) : 'Free (FedEx Express)')
    : (totals.shippingFeeToday > 0 ? formatMoney(totals.shippingFeeToday) : 'Free');

  return (
    <div className="cart-order-summary" data-testid="cart-order-summary">
      <h3 className="cart-order-summary-title">Order Summary</h3>
      {planPricing && (
        <div className="cart-summary-row">
          <span>Monthly price</span>
          <span>
            {formatMoney(planPricing.monthlyPrice)}/mo
            {isAnnual && <span className="cart-summary-note"> billed annually</span>}
          </span>
        </div>
      )}
      {totals.accessoriesTotal > 0 && (
        <div className="cart-summary-row">
          <span>Accessories</span>
          <span>{formatMoney(totals.accessoriesTotal)}</span>
        </div>
      )}
      <div className="cart-summary-row">
        <span>Shipping</span>
        <span>{shippingLabel}</span>
      </div>
      <div className="cart-summary-row cart-summary-total">
        <span>Due today</span>
        <span data-testid="cart-due-today">{formatMoney(totals.orderTotalToday)}</span>
      </div>
      <p className="cart-summary-footnote">
        No activation fee. {isAnnual ? 'Annual plans are paid in full today.' : 'Your plan renews monthly.'} Shipping options are chosen at checkout.
      </p>
    </div>
  );
};

const CartPage: React.FC = () => {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [productImages, setProductImages] = useState<{[key: string]: string}>({});
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [accessories, setAccessories] = useState<Accessory[]>([]);
  const [accessoryImages, setAccessoryImages] = useState<{[key: string]: string}>({});
  const [productsRetryCount, setProductsRetryCount] = useState(0);

  // Load cart items from storage on mount
  useEffect(() => {
    const items = cartStorage.getItems();
    setCartItems(items);
  }, []);

  // Reload cart items after adding/removing
  const refreshCartItems = () => {
    const items = cartStorage.getItems();
    setCartItems(items);
  };

  // Fetch products from database
  useEffect(() => {
    if (productsRetryCount > 0) setLoading(true);
    fetch(`${API_URL}/api/products`)
      .then(res => res.json())
      .then(data => {
        if (data.products) {
          setProducts(data.products);
        }
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching products:', error);
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsRetryCount]);

  // Fetch product images
  useEffect(() => {
    fetch(`${API_URL}/api/images`)
      .then(res => res.json())
      .then(data => {
        if (data.images && data.images.length > 0) {
          const images: {[key: string]: string} = {};
          
          // Map images by filename to product shortName
          products.forEach(product => {
            if (product.imageFilename) {
              const img = data.images.find((i: any) => 
                i.filename.toLowerCase() === product.imageFilename?.toLowerCase()
              );
              if (img) {
                images[product.shortName] = `${API_URL}/api/images/${img.id}`;
              }
            }
          });
          
          setProductImages(images);
        }
      })
      .catch(error => console.error('Error fetching images:', error));
  }, [products, API_URL]);

  // Fetch accessories from API
  useEffect(() => {
    fetch(`${API_URL}/api/accessories`)
      .then(res => res.json())
      .then(data => {
        if (data.accessories) {
          setAccessories(data.accessories);
        }
      })
      .catch(error => console.error('Error fetching accessories:', error));
  }, [API_URL]);

  // Fetch accessory images
  useEffect(() => {
    if (accessories.length > 0) {
      fetch(`${API_URL}/api/images`)
        .then(res => res.json())
        .then(data => {
          if (data.images && data.images.length > 0) {
            const images: {[key: string]: string} = {};
            accessories.forEach(accessory => {
              if (accessory.imageFilename) {
                const img = data.images.find((i: any) => 
                  i.filename.toLowerCase() === accessory.imageFilename?.toLowerCase()
                );
                if (img) {
                  images[accessory.id] = `${API_URL}/api/images/${img.id}`;
                }
              }
            });
            setAccessoryImages(images);
          }
        })
        .catch(error => console.error('Error fetching accessory images:', error));
    }
  }, [accessories, API_URL]);


  // Handle adding product to cart
  const handleAddProduct = () => {
    if (!selectedProduct || !selectedPlan) return;

    const product = products.find(p => p.shortName === selectedProduct);
    const plan = product?.pricingPlans.find(p => p.id === selectedPlan);
    
    if (!product || !plan) return;

    // Check if there's a protection plan in cart and if billing cycles match
    const existingCartItems = cartStorage.getItems();
    const existingProtectionPlan = existingCartItems.find(item => 
      item.type === 'accessory' && 
      item.name.toLowerCase().includes('protection plan')
    );

    if (existingProtectionPlan && existingProtectionPlan.billingCycle !== plan.billingCycle) {
      alert(`Protection plan must match service plan (monthly or annual). Your protection plan is ${existingProtectionPlan.billingCycle === 'annual' ? 'annual' : 'monthly'}, but you selected a ${plan.billingCycle === 'annual' ? 'annual' : 'monthly'} service plan.`);
      return;
    }

    const cartItem: CartItem = {
      id: `product-${product.id}-${plan.id}`,
      type: 'product',
      name: product.name,
      imageUrl: productImages[product.shortName],
      planId: plan.id,
      planName: plan.name,
      productId: product.id,
      productShortName: product.shortName
    };

    cartStorage.addItem(cartItem);

    const price = plan.annualTotal ?? plan.monthlyPrice;
    pushAddToCart({
      currency: 'USD',
      value: price ?? 0,
      items: [{
        item_id: cartItem.id,
        item_name: cartItem.name,
        price: price ?? 0,
        quantity: 1,
        index: 0,
      }],
    });

    refreshCartItems();
    setSelectedProduct(null);
    setSelectedPlan(null);
  };

  // Handle product selection change
  const handleProductChange = (shortName: string) => {
    setSelectedProduct(shortName);
    
    // Find the product and default to annual plan
    const product = products.find(p => p.shortName === shortName);
    if (product && product.pricingPlans.length > 0) {
      const annualPlan = product.pricingPlans.find(p => p.billingCycle === 'annual');
      setSelectedPlan(annualPlan?.id || product.pricingPlans[0].id);
    }
  };

  const isLifeBeaconProduct = (item: CartItem) =>
    item.type === 'product' && (item.productShortName === 'lifebeacon' || item.productShortName === 'lifebeacon-fall');

  const requiresCompatibleSystemAccessory = (name?: string) => {
    const lower = name?.toLowerCase() || '';
    if (!lower) return false;
    return lower.includes('smoke detector') ||
      lower.includes('necklace with help button') ||
      lower.includes('pendant with emergency call button') ||
      lower.includes('fall detection pendant') ||
      lower.includes('fall detection alert pendant') ||
      lower.includes('mobile fall detection') ||
      lower.includes('medical alert bracelet') ||
      (lower.includes('emergency button alarm') && lower.includes('bracelet'));
  };

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      alert('Your cart is empty. Please add items to continue.');
      return;
    }

    const lifeBeaconWithoutStyle = cartItems.find(item => isLifeBeaconProduct(item) && !item.deviceStyle);
    if (lifeBeaconWithoutStyle) {
      alert('Please choose Pendant or Watch for your LifeBeacon device.');
      return;
    }

    const hasPlanInCart = cartItems.some(item => item.type === 'product');
    const requiresSystemAccessoryInCart = cartItems.some(item => item.type === 'accessory' && requiresCompatibleSystemAccessory(item.name));
    if (!hasPlanInCart && requiresSystemAccessoryInCart) {
      const confirmed = window.confirm('Accessories like the Smoke Detector or pendant activators require an Alert1 medical alert system and plan. Please confirm you already have a compatible system before continuing to checkout.');
      if (!confirmed) {
        alert('Please add a compatible Alert1 system and plan before continuing to checkout.');
        navigate('/pricing#product-tiles');
        return;
      }
    }
    navigate('/checkout', { state: { cartItems } });
  };

  const selectedProductData = products.find(p => p.shortName === selectedProduct);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const selectedPlanData = selectedProductData?.pricingPlans.find(p => p.id === selectedPlan);

  // Check if there's already a product in cart
  const hasProductInCart = cartItems.some(item => item.type === 'product');
  const hasProtectionPlanInCart = cartItems.some(item => 
    item.type === 'accessory' && item.name.toLowerCase().includes('protection plan')
  );
  const requiresSystemAccessoryInCart = cartItems.some(item => item.type === 'accessory' && requiresCompatibleSystemAccessory(item.name));
  const accessoryOnlyCart = cartItems.length > 0 && !hasProductInCart && requiresSystemAccessoryInCart;

  // Price the product in the cart from the catalog, exactly as checkout prices it from /plans/:id.
  const productCartItem = cartItems.find(item => item.type === 'product');
  const findPlan = (planId?: string) =>
    planId ? products.flatMap(p => p.pricingPlans).find(pl => pl.id === planId) : undefined;
  const cartPlan = findPlan(productCartItem?.planId);
  const planPricing = cartPlan ? planPricingFromPlan(cartPlan) : null;
  const planPriceUnavailable = !!productCartItem && !cartPlan;
  const cartTotals = computeOrderTotals({ cartItems, planPricing, shippingMethod: 'express' });

  const renderItemPrice = (item: CartItem) => {
    if (item.type !== 'product') return null;
    const plan = findPlan(item.planId);
    if (!plan) {
      return <p className="cart-item-price cart-item-price--unavailable">Price unavailable</p>;
    }
    const pricing = planPricingFromPlan(plan);
    return (
      <p className="cart-item-price" data-testid="cart-item-price">
        <strong>{formatMoney(pricing.monthlyPrice)}/mo</strong>
        {pricing.billingCycle === 'annual'
          ? <span className="cart-item-price-note"> billed annually &middot; {formatMoney(pricing.todayTotal)} due today</span>
          : <span className="cart-item-price-note"> &middot; {formatMoney(pricing.todayTotal)} due today</span>}
      </p>
    );
  };

  const cartSummaryElement = cartItems.length > 0 ? (
    <CartOrderSummary
      totals={cartTotals}
      planPricing={planPricing}
      planPriceUnavailable={planPriceUnavailable}
      onRetryPricing={() => setProductsRetryCount(c => c + 1)}
    />
  ) : null;

  // Filter accessories - exclude protection plan if already in cart
  const availableAccessories = accessories.filter(acc => {
    if (acc.name.toLowerCase().includes('protection plan') && hasProtectionPlanInCart) {
      return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="cart-page">
        <div className="loading-container" style={{ padding: '4rem', textAlign: 'center' }}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      {/* Hero Section */}
      <section className="cart-hero">
        <div className="container">
          <h1>Your Shopping Cart</h1>
        </div>
      </section>

      {/* Cart Items Section */}
      {cartItems.length > 0 && (
        <section className="cart-items-section" style={{ padding: '2rem 0' }}>
          <div className="container">
            <h2>Items in Your Cart ({cartItems.length})</h2>
            {accessoryOnlyCart && (
              <div
                style={{
                  marginTop: '1rem',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeeba',
                  borderRadius: '8px',
                  padding: '1rem',
                  color: '#856404',
                  lineHeight: 1.6
                }}
              >
                <strong>No medical alert system or plan chosen.</strong> Accessories must be accompanied by a system and plan combination.
              </div>
            )}
            
            <div className="cart-items-list" style={{ marginTop: '2rem' }}>
              {cartItems.map((item, index) => (
                <div key={item.id} className="cart-item" style={{ 
                  position: 'relative',
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  padding: '1.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  background: 'white'
                }}>
                  {/* X button in top-right corner */}
                  <button
                    onClick={() => {
                      cartStorage.removeItemById(item.id);
                      refreshCartItems();
                    }}
                    style={{
                      position: 'absolute',
                      top: '0.5rem',
                      right: '0.5rem',
                      width: '28px',
                      height: '28px',
                      padding: '0',
                      backgroundColor: 'transparent',
                      color: '#999',
                      border: 'none',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      fontSize: '1.2rem',
                      lineHeight: '1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f0f0f0';
                      e.currentTarget.style.color = '#dc3545';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#999';
                    }}
                    aria-label="Remove item"
                    title="Remove item"
                  >
                    ×
                  </button>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                    {item.imageUrl && (
                      <img 
                        src={item.imageUrl} 
                        alt={item.name}
                        style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '4px' }}
                      />
                    )}
                    <div>
                      <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.2rem' }}>{item.name}</h3>
                      {item.type === 'product' && item.planName && (
                        <p style={{ margin: '0', color: '#666', fontSize: '0.9rem' }}>{item.planName}</p>
                      )}
                      {renderItemPrice(item)}
                      {item.type === 'accessory' && item.billingCycle && (
                        <p style={{ margin: '0', color: '#666', fontSize: '0.9rem' }}>
                          {item.billingCycle === 'annual' ? 'Annual' : 'Monthly'} Plan
                        </p>
                      )}
                      {item.price && (
                        <p style={{ margin: '0.5rem 0 0 0', fontWeight: 'bold', fontSize: '1.1rem' }}>
                          ${item.price.toFixed(2)}
                          {item.billingCycle === 'annual' ? '/year' : '/month'}
                        </p>
                      )}
                      {isLifeBeaconProduct(item) && (
                        <div style={{ marginTop: '0.75rem' }}>
                          <span style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: 600 }}>Wear as:</span>
                          <label style={{ marginRight: '1rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                            <input
                              type="radio"
                              name={`deviceStyle-${item.id}`}
                              checked={item.deviceStyle === 'pendant'}
                              onChange={() => {
                                const updated = { ...item, deviceStyle: 'pendant' as const };
                                cartStorage.addItem(updated);
                                refreshCartItems();
                              }}
                            />
                            {' '}Pendant
                          </label>
                          <label style={{ cursor: 'pointer', fontSize: '0.9rem' }}>
                            <input
                              type="radio"
                              name={`deviceStyle-${item.id}`}
                              checked={item.deviceStyle === 'watch'}
                              onChange={() => {
                                const updated = { ...item, deviceStyle: 'watch' as const };
                                cartStorage.addItem(updated);
                                refreshCartItems();
                              }}
                            />
                            {' '}Watch
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {cartItems.some(item => isLifeBeaconProduct(item) && !item.deviceStyle) && (
              <div
                style={{
                  marginTop: '1rem',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeeba',
                  borderRadius: '8px',
                  padding: '1rem',
                  color: '#856404',
                  lineHeight: 1.6
                }}
              >
                Please choose Pendant or Watch for your LifeBeacon device above before checkout.
              </div>
            )}

            <div className="cart-checkout-row" style={{ marginTop: '2rem', textAlign: 'right' }}>
              {cartSummaryElement}
              <button
                className="checkout-button"
                onClick={handleCheckout}
                disabled={cartItems.some(item => isLifeBeaconProduct(item) && !item.deviceStyle)}
                style={{
                  padding: '1rem 2rem',
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  backgroundColor: cartItems.some(item => isLifeBeaconProduct(item) && !item.deviceStyle) ? '#ccc' : '#c8102e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: cartItems.some(item => isLifeBeaconProduct(item) && !item.deviceStyle) ? 'not-allowed' : 'pointer'
                }}
              >
                GO TO SECURE CHECKOUT
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Add Product Section - only show if no product in cart */}
      {!hasProductInCart && (
        <section className="product-selection" style={{ padding: '2rem 0', background: cartItems.length > 0 ? '#f9f9f9' : 'white' }}>
          <div className="container">
            <h2>Select your medical alert system:</h2>
          
          <div className="product-grid">
            {products.map(product => (
              <div 
                key={product.shortName}
                className={`product-option ${selectedProduct === product.shortName ? 'selected' : ''}`}
                onClick={() => handleProductChange(product.shortName)}
              >
                <div className="product-image-wrapper">
                  {productImages[product.shortName] ? (
                    <img src={productImages[product.shortName]} alt={product.name} />
                  ) : (
                    <div className="product-placeholder"></div>
                  )}
                </div>
                <div className="product-radio">
                  <input 
                    type="radio" 
                    id={product.shortName}
                    name="product" 
                    value={product.shortName}
                    checked={selectedProduct === product.shortName}
                    onChange={() => {}}
                    readOnly
                  />
                  <label htmlFor={product.shortName}>{product.name}</label>
                </div>
              </div>
            ))}
          </div>

      {/* Subscription Plan Selection */}
      {selectedProduct && selectedProductData && (
            <div style={{ marginTop: '2rem', padding: '2rem', background: 'white', borderRadius: '8px' }}>
              <h3>Select your subscription plan:</h3>
              
              <div className="plan-options" style={{ marginTop: '1rem' }}>
                {selectedProductData.pricingPlans.map(plan => (
                  <div key={plan.id} className="plan-option">
                    <input 
                      type="radio" 
                      id={plan.id}
                      name="plan"
                      value={plan.id}
                      checked={selectedPlan === plan.id}
                      onChange={() => setSelectedPlan(plan.id)}
                    />
                    <label htmlFor={plan.id}>
                      <span className="plan-name">{plan.name}</span>
                      <span className="plan-price">
                        <strong>${plan.monthlyPrice.toFixed(2)}</strong> per month
                      </span>
                    </label>
                  </div>
                ))}
              </div>
              
              <button
                className="add-to-cart-button"
                disabled={!selectedPlan}
                onClick={handleAddProduct}
                style={{
                  marginTop: '1.5rem',
                  padding: '0.75rem 1.5rem',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  backgroundColor: selectedPlan ? '#28a745' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: selectedPlan ? 'pointer' : 'not-allowed'
                }}
              >
                ADD TO CART
              </button>
            </div>
          )}
          
          {/* Prompt to select a product if cart is empty */}
          {cartItems.length === 0 && !selectedProduct && (
            <div className="selection-prompt" style={{ marginTop: '2rem', textAlign: 'center' }}>
              <p>👆 Please select a medical alert system above to continue</p>
            </div>
          )}

          {/* Checkout button when cart has items but no product selected */}
          {cartItems.length > 0 && !selectedProduct && (
            <div style={{ marginTop: '2rem', textAlign: 'center' }}>
              {accessoryOnlyCart && (
                <div
                  style={{
                    marginBottom: '1.5rem',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffeeba',
                    borderRadius: '8px',
                    padding: '1rem',
                    color: '#856404',
                    lineHeight: 1.6
                  }}
                >
                  <strong>No medical alert system or plan chosen.</strong> Accessories must be accompanied by a system and plan combination.
                </div>
              )}
              <button 
                className="checkout-button"
                onClick={handleCheckout}
                style={{
                  padding: '1rem 2rem',
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  backgroundColor: '#c8102e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                GO TO SECURE CHECKOUT
              </button>
            </div>
          )}
          </div>
        </section>
      )}

      {/* Multiple Devices Message - show if product already in cart */}
      {hasProductInCart && (
        <section style={{ padding: '2rem 0', background: '#fff3cd', textAlign: 'center' }}>
          <div className="container">
            <p style={{ fontSize: '1.1rem', margin: 0 }}>
              Need more than one device? Call us today at <a href="tel:1-877-579-9862" style={{ color: '#28a745', fontWeight: 'bold', textDecoration: 'underline' }}>1-877-579-9862</a> to purchase a second, discounted plan!
            </p>
          </div>
        </section>
      )}

      {/* Accessories Section */}
      {availableAccessories.length > 0 && (
        <section className="accessories-section" style={{ padding: '2rem 0', background: '#f9f9f9' }}>
          <div className="container">
            <h2 style={{ marginBottom: '1.5rem' }}>Add Accessories</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1.5rem' }}>
              {availableAccessories.map(accessory => {
                const accessorySlug = accessory.shortName || accessory.id;
                const accessoryLink = `/accessories/${accessorySlug}`;
                const accessoryImage = accessoryImages[accessory.id];
                
                return (
                  <Link
                    key={accessory.id}
                    to={accessoryLink}
                    style={{
                      textDecoration: 'none',
                      color: 'inherit',
                      display: 'block'
                    }}
                  >
                    <div style={{
                      background: 'white',
                      borderRadius: '8px',
                      padding: '1rem',
                      border: '1px solid #ddd',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    >
                      {accessoryImage && (
                        <img 
                          src={accessoryImage} 
                          alt={accessory.name}
                          style={{ 
                            width: '100%', 
                            height: '150px', 
                            objectFit: 'contain', 
                            marginBottom: '0.5rem',
                            borderRadius: '4px'
                          }}
                        />
                      )}
                      <h3 style={{ 
                        margin: '0 0 0.5rem 0', 
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        color: '#333'
                      }}>
                        {accessory.name}
                      </h3>
                      {accessory.description && (
                        <p style={{ 
                          margin: '0 0 0.5rem 0', 
                          fontSize: '0.85rem', 
                          color: '#666',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          {accessory.description}
                        </p>
                      )}
                      <p style={{ 
                        margin: '0', 
                        fontSize: '1rem', 
                        fontWeight: 'bold',
                        color: '#28a745'
                      }}>
                        ${accessory.price.toFixed(2)}
                        {accessory.priceType === 'monthly' ? '/month' : accessory.priceType === 'annual' ? '/year' : ''}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default CartPage;
